import React, { useEffect } from 'react';
import useSiteKey from '../../util/useSiteKey/useSiteKey';

// Shared instance tracking at the module level to prevent race conditions during SPA page transitions
let activeInstances = 0;

// Helper to check if grecaptcha enterprise is defined
const isGrecaptchaLoaded = () => {
  return !!(window.grecaptcha && window.grecaptcha.enterprise);
};

// Helper to inject the explicit reCAPTCHA Enterprise script tag
const injectScript = () => {
  const script = document.createElement('script');
  script.id = 'recaptcha-enterprise-script';
  script.src =
    'https://www.google.com/recaptcha/enterprise.js?onload=onloadCallback&render=explicit';
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
};

// Helper to safely execute render using ready() if available, or direct execution
const executeReadyOrRender = callback => {
  const readyFn = window.grecaptcha?.enterprise?.ready;
  if (typeof readyFn === 'function') {
    readyFn(callback);
  } else {
    callback();
  }
};

// Global submit callback to dispatch the recaptcha token ready event
const cbRecaptchaSubmit = token => {
  const evt = document.createEvent('Event');
  evt.initEvent('rTokenRdy', true, true);
  evt.token = token;
  document.dispatchEvent(evt);
};

// Render the recaptcha widget inside the given element ID
const renderRecaptcha = (id, siteKey, action) => {
  if (isGrecaptchaLoaded()) {
    try {
      window.grecaptcha.enterprise.render(id, {
        sitekey: siteKey,
        action: action,
        callback: window.cbRecaptchaSubmit
      });
    } catch (e) {
      console.error('reCAPTCHA render error:', e);
    }
  }
};

// Perform complete global state cleanup when all components unmount
const performGlobalCleanup = () => {
  delete window.cbRecaptchaSubmit;
  delete window.onloadCallback;

  const existingScript = document.getElementById('recaptcha-enterprise-script');
  if (existingScript && existingScript.parentNode) {
    existingScript.parentNode.removeChild(existingScript);
  }

  window.grecaptcha = undefined;
};

function RegisterReCaptcha({ action, id = 'RegisterReCaptcha' }) {
  const siteKey = useSiteKey(2);

  useEffect(() => {
    window.localStorage.removeItem('token');
    activeInstances++;

    // Clear window.grecaptcha if it was loaded by an external script (such as Login page or GTM)
    const ourScript = document.getElementById('recaptcha-enterprise-script');
    if (window.grecaptcha && !ourScript) {
      window.grecaptcha = undefined;
    }

    // Set the global callbacks
    window.cbRecaptchaSubmit = cbRecaptchaSubmit;
    window.onloadCallback = () => renderRecaptcha(id, siteKey, action);

    // If already loaded, render immediately; otherwise inject the script
    if (isGrecaptchaLoaded()) {
      executeReadyOrRender(() => renderRecaptcha(id, siteKey, action));
    } else {
      const script = document.getElementById('recaptcha-enterprise-script');
      if (!script) {
        injectScript();
      }
    }

    return () => {
      activeInstances--;
      if (activeInstances === 0) {
        performGlobalCleanup();
      }
    };
  }, [siteKey, action, id]);

  return <div id={id} data-testid="recaptcha"></div>;
}

export default RegisterReCaptcha;
